/*
 * "Add user": spend one add-user credit to ask Wealthoak to put someone under
 * you. POST /partner/added-users. Wealthoak confirms the person is yours and
 * links their client account (they then appear under Clients and earn you
 * commission), or rejects the request and the credit comes back.
 *
 * AddUserButton is the entry point used on the Overview and Referrals pages:
 * it opens the sheet, then re-reads /me so the credit counts update.
 */

import { useState } from "react";
import { ApiError, addedUsers, auth, errorMessage } from "../api/client";
import { useAuth, useMe } from "../auth/AuthContext";
import { Dialog } from "./bits";
import { plural } from "./format";
import { Info, UserPlus } from "./icons";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NOTES_MAX = 1000;

export function AddUserButton({
  onAdded,
  className = "button button--secondary",
}: {
  onAdded?: (name: string) => void;
  className?: string;
}) {
  const me = useMe();
  const { setMe } = useAuth();
  const [open, setOpen] = useState(false);
  const available = me.user_credits.available;

  return (
    <>
      <button
        className={className}
        onClick={() => setOpen(true)}
        disabled={available < 1}
        title={available < 1 ? "You earn an add-user credit when a client who used your link pays for their plan" : undefined}
      >
        <UserPlus size={15} /> Add user{available > 0 ? ` (${available})` : ""}
      </button>
      {open && (
        <AddUserSheet
          onClose={() => setOpen(false)}
          onSubmitted={(name) => {
            setOpen(false);
            void auth.me().then(setMe).catch(() => undefined);
            onAdded?.(name);
          }}
        />
      )}
    </>
  );
}

export default function AddUserSheet({
  onClose,
  onSubmitted,
}: {
  onClose: () => void;
  onSubmitted: (name: string) => void;
}) {
  const me = useMe();
  const inactive = me.status !== "active";
  const credits = me.user_credits;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameError = !name.trim() ? "Enter their name." : null;
  const emailError = !email.trim() ? "Enter their email." : !EMAIL.test(email.trim()) ? "That doesn't look like an email address." : null;
  const phoneError = phone.trim() && !/^[+\d][\d\s-]{6,19}$/.test(phone.trim()) ? "Use digits, spaces or dashes, e.g. +91 98200 12345." : null;
  const invalid = Boolean(nameError || emailError || phoneError);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (invalid || inactive || credits.available < 1) return;
    setBusy(true);
    setError(null);
    try {
      const created = await addedUsers.create({
        name: name.trim(),
        email: email.trim(),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onSubmitted(created.lead_name);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      variant="sheet"
      title="Add a user"
      description="Uses 1 add-user credit. Wealthoak confirms this person is your client and adds them under you. If we can't, the credit comes back to you."
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      {inactive ? (
        <div className="notice is-warn" role="status">
          <Info size={16} />
          <span>Your partner account is awaiting approval. You can add users once Wealthoak approves it.</span>
        </div>
      ) : (
        <div className={`notice${credits.available < 1 ? " is-warn" : ""}`} role="status">
          <Info size={16} />
          <span>
            You have <strong>{plural(credits.available, "credit")}</strong> available
            {credits.pending ? `, ${credits.pending} in review` : ""}. You earn one each time a client who came through
            your referral link pays for their plan.
          </span>
        </div>
      )}
      <form onSubmit={submit} noValidate>
        <div className="form-grid">
          <div className="field is-wide">
            <label className="field__label" htmlFor="add-name">Full name</label>
            <input
              id="add-name"
              className="input"
              autoComplete="off"
              value={name}
              maxLength={120}
              aria-invalid={touched && Boolean(nameError)}
              aria-describedby={touched && nameError ? "add-name-err" : undefined}
              onChange={(e) => setName(e.target.value)}
              placeholder="Neha Joshi"
              data-autofocus
            />
            {touched && nameError && <p id="add-name-err" className="field__error">{nameError}</p>}
          </div>
          <div className="field is-wide">
            <label className="field__label" htmlFor="add-email">Email</label>
            <input
              id="add-email"
              className="input"
              type="email"
              autoComplete="off"
              value={email}
              maxLength={254}
              aria-invalid={touched && Boolean(emailError)}
              aria-describedby={touched && emailError ? "add-email-err" : "add-email-hint"}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="neha@example.com"
            />
            {touched && emailError ? (
              <p id="add-email-err" className="field__error">{emailError}</p>
            ) : (
              <p id="add-email-hint" className="field__hint">The email they use with Wealthoak, so we can find their account.</p>
            )}
          </div>
          <div className="field is-wide">
            <label className="field__label" htmlFor="add-phone">
              Phone <em>(optional)</em>
            </label>
            <input
              id="add-phone"
              className="input"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              value={phone}
              maxLength={20}
              aria-invalid={touched && Boolean(phoneError)}
              aria-describedby={touched && phoneError ? "add-phone-err" : undefined}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98200 12345"
            />
            {touched && phoneError && <p id="add-phone-err" className="field__error">{phoneError}</p>}
          </div>
          <div className="field is-wide">
            <label className="field__label" htmlFor="add-notes">
              Note for the Wealthoak team <em>(optional)</em>
            </label>
            <textarea
              id="add-notes"
              className="input"
              value={notes}
              maxLength={NOTES_MAX}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="How you know them, when they joined…"
            />
          </div>
        </div>

        {error && <div className="form-error" role="alert">{error}</div>}

        <div className="form-actions">
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={busy || inactive || credits.available < 1}>
            {busy ? "Sending…" : "Use 1 credit & send"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
